import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { message } from 'antd'
import api from '../utils/api'

const LoginPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #e8e8e8 0%, #d4c4b0 50%, #c4b5c0 100%);
  padding: 20px;

  @media (max-width: 768px) {
    padding: 20px 16px;
    box-sizing: border-box;
  }
`

const FormContainer = styled.form`
  background: #faf9f6;
  padding: 40px;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(154, 163, 175, 0.2);
  width: 100%;
  max-width: 450px;
  text-align: center;
  border: 1px solid rgba(168, 181, 192, 0.3);

  @media (max-width: 768px) {
    padding: 30px 20px;
    width: 100%;
    max-width: 100%;
    margin: 0;
    box-sizing: border-box;
  }
`

const Logo = styled.img`
  width: 80px;
  height: 80px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
  }
`

const Title = styled.h1`
  font-size: 28px;
  margin-bottom: 10px;
  color: #5a5a5a;
  font-weight: 500;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`

const Subtitle = styled.p`
  color: #8a8a8a;
  font-size: 16px;
  margin-bottom: 30px;
  font-weight: 300;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 25px;
  }
`

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  margin: 12px 0;
  border: 1.5px solid #d4c4b0;
  border-radius: 12px;
  box-sizing: border-box;
  font-size: 16px;
  transition: all 0.3s ease;
  background-color: #ffffff;
  color: #4a4a4a;

  &:focus {
    outline: none;
    border-color: #a8b5c0;
    box-shadow: 0 0 0 3px rgba(168, 181, 192, 0.15);
  }

  &::placeholder {
    color: #b8b8b8;
  }

  @media (max-width: 768px) {
    padding: 12px 14px;
    font-size: 15px;
  }
`

const Button = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #a8b5c0 0%, #9ca3af 100%);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-size: 18px;
  font-weight: 500;
  transition: all 0.3s ease;
  margin-top: 10px;
  letter-spacing: 0.5px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(156, 163, 175, 0.3);
    background: linear-gradient(135deg, #9ca3af 0%, #8a939f 100%);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 12px;
    font-size: 16px;
  }
`

const ErrorMessage = styled.p`
  color: #c89b9b;
  font-size: 14px;
  margin-top: 10px;
  text-align: center;
  font-weight: 400;
`

const Footer = styled.p`
  color: #9a9a9a;
  font-size: 14px;
  margin-top: 20px;
  text-align: center;
  font-weight: 300;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const SubscriptionLoginPage = () => {
  const [loginData, setLoginData] = useState({
    merchantOrderNo: '',
    password: ''
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  // 检查是否已经登录（有token）
  React.useEffect(() => {
    const token = sessionStorage.getItem('subscription_view_token')
    if (token) {
      // 如果已有token，直接跳转到列表页面
      navigate('/subscription/list')
    }
  }, [navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!loginData.merchantOrderNo || !loginData.password) {
      setError('請輸入帳號和密碼')
      return
    }

    setLoading(true)

    try {
      const response = await api.post('/api/subscription/login', {
        merchantOrderNo: loginData.merchantOrderNo,
        password: loginData.password
      })

      if (response.data.success) {
        // 保存token到sessionStorage
        sessionStorage.setItem('subscription_view_token', response.data.token)
        message.success('登入成功')
        // 跳转到订阅列表页面
        navigate('/subscription/list')
      } else {
        setError(response.data.message || '登入失敗，請重新嘗試')
      }
    } catch (error) {
      console.error('登入錯誤:', error)
      const errorMessage =
        error.response?.data?.message || '登入失敗，請檢查帳號密碼是否正確'
      setError(errorMessage)
      message.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <LoginPageContainer>
      <FormContainer onSubmit={handleSubmit}>
        <Logo src={'/saa.png'} alt='公司logo' />
        <Title>訂閱資訊查詢系統</Title>
        <Subtitle>合作夥伴專用登入頁面</Subtitle>
        <Input
          type='text'
          value={loginData.merchantOrderNo}
          onChange={(e) =>
            setLoginData({ ...loginData, merchantOrderNo: e.target.value })
          }
          placeholder='帳號 (Merchant Order No)'
          required
          disabled={loading}
        />
        <Input
          type='password'
          value={loginData.password}
          onChange={(e) =>
            setLoginData({ ...loginData, password: e.target.value })
          }
          placeholder='密碼'
          required
          disabled={loading}
        />
        <Button type='submit' disabled={loading}>
          {loading ? '登入中...' : '登入'}
        </Button>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <Footer>美感 | BeauteFeel</Footer>
      </FormContainer>
    </LoginPageContainer>
  )
}

export default SubscriptionLoginPage
